import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import type { Game } from '@/redux/reducers/gameReducer'
import LottoCountryCard, {
  LottoCountryCardProps,
} from '@/ui-components/components/LottoCountryCard'
import { DATE_FORMAT_HUMAN } from '@/utils/date-time-utils'
import { getLottoNameAndCountryFromLottoGame } from '@/utils/lotto-type-v2'
import { getBackgroundFlagGroup } from '@/utils/flag-utils'
import { formatDateTime, getResultNumber } from './result-card.utils'
import { TFunction } from 'i18next'
import { getGameWithRoundTitle } from '@/utils/gameUtils'

export const LottoCountryResultCard = ({
  game,
  rounds,
}: {
  game: Game
  rounds: Game['rounds']
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const round = rounds?.[0]
  const closeAt = round?.close_at
  const { country, name: flagBackgroundName } =
    getLottoNameAndCountryFromLottoGame(game.type, game.flag)
  const title = getGameWithRoundTitle(i18n, game, round)
  const noRound = game.rounds?.length === 0
  const onClick = () => {
    if (round?.id) {
      navigate(`/lotto/${game.code}/${game.id}/${round.id}`)
    } else {
      toast.error(t('message.no_round'))
    }
  }

  const buildLottoCountrySections = (
    round: Game['rounds'][0],
    t: TFunction,
  ): LottoCountryCardProps['sections'] => {
    const sections: LottoCountryCardProps['sections'] = []
    const noRoundMessage = t('message.no_round') || 'No Round'

    if (!round) {
      return [
        {
          title: t('betDetailPanel.betLottoType.bet_three_top'),
          numbers: [noRoundMessage],
        },
        {
          title: t('betDetailPanel.betLottoType.bet_two_under'),
          numbers: [noRoundMessage],
        },
      ]
    }

    if (round.top_three_result) {
      sections.push({
        title: t('betDetailPanel.betLottoType.bet_three_top'),
        numbers: getResultNumber(round.top_three_result),
      })
    }

    if (round.bottom_two_result) {
      sections.push({
        title: t('betDetailPanel.betLottoType.bet_two_under'),
        numbers: getResultNumber(round.bottom_two_result),
      })
    }

    if (!round?.is_result_confirmed) {
      return sections.map((i) => ({
        ...i,
        result: t('betResult.waiting'),
      }))
    }
    return sections
  }

  return (
    <LottoCountryCard
      key={`lotto-country-${game.id}`}
      country={country}
      flagBackgroundName={getBackgroundFlagGroup(flagBackgroundName)}
      title={title}
      date={formatDateTime(DATE_FORMAT_HUMAN, closeAt, i18n.language)}
      time={formatDateTime('HH:mm', closeAt, i18n.language)}
      sections={buildLottoCountrySections(round, t)}
      noRound={noRound}
      onClick={onClick}
    />
  )
}
